import { RefObject, useEffect, useMemo, useState } from 'react';
import { View } from 'react-vega';
import { Spec } from 'vega';

import { VegaChartSignal } from './vegaChartSignal';

const viewHasSignal = (view: View, name: string): boolean => {
  try {
    view.signal(name);
    return true;
  } catch {
    return false;
  }
};

export const useHeightPerEntry = (
  spec: Spec,
  vegaViewRef: RefObject<View | null>
) => {
  const [hasDataRecordHeight, initialValue] = useMemo(() => {
    const signal = spec.signals?.find(
      (s) => s.name === VegaChartSignal.dataRecordHeight
    );

    const hasDataRecordHeight = !!signal;
    const initialValue =
      hasDataRecordHeight &&
      'value' in signal &&
      typeof signal.value === 'number'
        ? signal.value
        : 0;

    return [hasDataRecordHeight, initialValue];
  }, [spec]);

  const [heightPerEntry, setHeightPerEntry] = useState(initialValue);

  useEffect(() => {
    if (!hasDataRecordHeight) {
      return;
    }

    const view = vegaViewRef.current;
    if (!view || !viewHasSignal(view, VegaChartSignal.dataRecordHeight)) {
      return;
    }

    // Vega invokes listeners as (name, value).
    const handler = (_name: string, value: unknown) => {
      if (typeof value === 'number') {
        setHeightPerEntry(value);
      }
    };

    view.addSignalListener(VegaChartSignal.dataRecordHeight, handler);

    return () => {
      if (!viewHasSignal(view, VegaChartSignal.dataRecordHeight)) {
        return;
      }
      view.removeSignalListener(VegaChartSignal.dataRecordHeight, handler);
    };
  });

  return heightPerEntry;
};
